import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Play, Plus, Check, Star, Clock, Calendar, Loader2, Film, Crown } from "lucide-react";
import Row from "@/components/Row";
import HlsVideo from "@/components/HlsVideo";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [inList, setInList] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [ents, setEnts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/content/${id}`).then((r) => {
      setItem(r.data);
      if (r.data.genres?.[0]) {
        api.get("/content", { params: { genre: r.data.genres[0] } }).then((res) =>
          setRelated(res.data.filter((c) => c.id !== id).slice(0, 12))
        );
      }
    }).catch(() => setItem(false));
    if (user) {
      api.get("/mylist").then((r) => setInList(r.data.some((c) => c.id === id))).catch(() => {});
      api.get("/entitlements").then((r) => setEnts(r.data)).catch(() => {});
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps -- reload on id/auth change

  const owns = (episodeId = null) => ents.some((e) =>
    e.content_id === id && (e.episode_id || null) === (episodeId || null) &&
    (e.kind === "buy" || (e.expires_at && new Date(e.expires_at) > new Date())));

  const buy = async (mode, episodeId = null) => {
    if (!user) { toast.error("Sign up free to rent or buy"); navigate("/login", { state: { from: `/title/${id}` } }); return; }
    try {
      const { data } = await api.post("/checkout", {
        content_id: id, episode_id: episodeId, mode, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout"); }
  };

  const toggleList = async () => {
    if (!user) return toast.error("Sign in to manage your list");
    try {
      if (inList) {
        await api.delete(`/mylist/${id}`);
        setInList(false);
        toast.success("Removed from My List");
      } else {
        await api.post("/mylist", { content_id: id });
        setInList(true);
        toast.success("Added to My List");
      }
    } catch { toast.error("Action failed"); }
  };

  const valid = item && typeof item === "object";
  useSeo({
    title: valid ? (item.seo_title || item.title) : undefined,
    description: valid ? (item.seo_description || item.description) : undefined,
    keywords: valid ? (item.seo_keywords || (item.genres || []).join(", ")) : undefined,
    image: valid ? item.poster_url : undefined,
    type: "video.movie",
    jsonLd: valid ? {
      "@context": "https://schema.org",
      "@type": item.type === "series" ? "TVSeries" : "Movie",
      name: item.title,
      description: item.description,
      image: item.poster_url,
      datePublished: String(item.release_year),
      genre: item.genres,
      director: item.director ? { "@type": "Person", name: item.director } : undefined,
      actor: (item.cast || []).map((c) => ({ "@type": "Person", name: c })),
      aggregateRating: item.rating ? { "@type": "AggregateRating", ratingValue: item.rating, bestRating: 10, ratingCount: 100 } : undefined,
    } : null,
  });

  if (item === null) return <div className="min-h-screen flex items-center justify-center bg-ink"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;
  if (item === false) return <div className="min-h-screen flex items-center justify-center bg-ink text-white/60">Title not found.</div>;

  const movieFree = item.type === "movie" && (Number(item.rent_price) <= 0 || Number(item.buy_price) <= 0);

  return (
    <div className="bg-ink min-h-screen pb-20">
      <div className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img src={item.backdrop_url} alt={item.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 to-transparent" />
        </div>
        <div className="relative z-10 px-4 sm:px-12 pb-12 max-w-3xl animate-fade-up">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none mb-4">{item.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-white/80 mb-5">
            <span className="text-gold flex items-center gap-1 font-semibold"><Star className="w-4 h-4 fill-gold" /> {item.rating}</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {item.release_year}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {item.runtime} min</span>
            <span className="border border-white/30 px-1.5 rounded text-xs">{item.maturity}</span>
            {movieFree && <span data-testid="detail-free-badge" className="bg-gold text-ink font-bold px-2 py-0.5 rounded text-xs uppercase tracking-wide">Free</span>}
            <span className="capitalize flex items-center gap-1"><Film className="w-4 h-4" /> {item.type}</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {item.genres?.map((g) => (
              <span key={g} className="text-xs bg-white/10 px-3 py-1 rounded-full">{g}</span>
            ))}
          </div>
          <p className="text-white/85 leading-relaxed mb-6 max-w-2xl">{item.description}</p>
          <div className="flex flex-wrap items-center gap-3">
            {item.type === "movie" && (owns() || movieFree) && (
              <button data-testid="detail-play-btn" onClick={() => navigate(`/watch/${item.id}`)} className="bg-white text-black hover:bg-white/90 rounded-md font-semibold px-7 py-2.5 flex items-center gap-2 transition-colors">
                <Play className="w-5 h-5 fill-black" /> {movieFree && !owns() ? "Play Free" : "Play"}
              </button>
            )}
            {item.type === "movie" && !owns() && !movieFree && (
              <>
                <button data-testid="rent-btn" onClick={() => buy("rent")} className="bg-brand hover:bg-brand/90 rounded-md font-semibold px-6 py-2.5 flex items-center gap-2 transition-colors">
                  <Clock className="w-5 h-5" /> Rent ${item.rent_price} for {item.rent_days} Days
                </button>
                <button data-testid="buy-btn" onClick={() => buy("buy")} className="bg-gold text-black hover:bg-gold/90 rounded-md font-semibold px-6 py-2.5 flex items-center gap-2 transition-colors">
                  <Crown className="w-5 h-5" /> Buy ${item.buy_price}
                </button>
              </>
            )}
            {item.type === "series" && item.episodes?.some((e) => owns(e.id)) && (
              <button data-testid="detail-play-btn" onClick={() => { const ep = item.episodes.find((e) => owns(e.id)); navigate(`/watch/${item.id}?ep=${ep.id}`); }} className="bg-white text-black hover:bg-white/90 rounded-md font-semibold px-7 py-2.5 flex items-center gap-2 transition-colors">
                <Play className="w-5 h-5 fill-black" /> Play
              </button>
            )}
            {item.type === "series" && !item.episodes?.some((e) => owns(e.id)) && (
              <button data-testid="series-episodes-btn" onClick={() => document.getElementById("episodes")?.scrollIntoView({ behavior: "smooth" })} className="bg-white text-black hover:bg-white/90 rounded-md font-semibold px-7 py-2.5 flex items-center gap-2 transition-colors">
                <Play className="w-5 h-5 fill-black" /> Choose an Episode
              </button>
            )}
            <button data-testid="detail-trailer-btn" onClick={() => setShowTrailer(true)} className="bg-white/15 backdrop-blur-md hover:bg-white/25 rounded-md font-semibold px-6 py-2.5 flex items-center gap-2 transition-colors">
              <Film className="w-5 h-5" /> Trailer
            </button>
            <button data-testid="detail-addlist-btn" onClick={toggleList} className="border border-white/40 hover:border-white rounded-md font-semibold px-5 py-2.5 flex items-center gap-2 transition-colors">
              {inList ? <Check className="w-5 h-5 text-gold" /> : <Plus className="w-5 h-5" />}
              {inList ? "In My List" : "My List"}
            </button>
          </div>
          {item.type === "series" && (
            <p data-testid="series-pricing-hint" className="text-white/60 text-sm mt-3">
              Rent each episode for ${item.rent_price} ({item.rent_days} days) or buy for ${item.buy_price}. Choose an episode below.
            </p>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-12 mt-8 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-4">
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white/40 mb-1">Cast</h3>
            <p className="text-white/90">{item.cast?.join(", ")}</p>
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white/40 mb-1">Director</h3>
            <p className="text-white/90">{item.director}</p>
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-white/40">Available in</h3>
          <div className="flex flex-wrap gap-2">
            {item.qualities?.map((q) => <span key={q} className="text-xs bg-gold/15 text-gold px-3 py-1 rounded-full font-semibold">{q}</span>)}
          </div>
          <h3 className="text-xs uppercase tracking-widest text-white/40 pt-2">Audio</h3>
          <p className="text-white/80 text-sm">{item.audio_tracks?.join(", ")}</p>
          <h3 className="text-xs uppercase tracking-widest text-white/40 pt-2">Subtitles</h3>
          <p className="text-white/80 text-sm">{item.subtitles?.filter((s) => s !== "Off").join(", ")}</p>
        </div>
      </div>

      {item.type === "series" && item.episodes?.length > 0 && (
        <div id="episodes" className="px-4 sm:px-12 mt-10">
          <h2 className="font-display text-2xl font-bold mb-4">Episodes</h2>
          <div className="space-y-3">
            {item.episodes.map((ep) => {
              const epRent = ep.rent_price ?? item.rent_price;
              const epBuy = ep.buy_price ?? item.buy_price;
              const ownEp = owns(ep.id);
              return (
                <div key={ep.id} data-testid={`episode-${ep.id}`} className="flex items-center gap-4 bg-surface rounded-lg p-3">
                  <div className="text-white/40 font-display font-bold text-xl w-8 text-center">{ep.episode}</div>
                  <img src={ep.thumbnail} alt={ep.title} className="w-32 sm:w-40 aspect-video object-cover rounded-md shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{ep.title}</p>
                    <p className="text-white/50 text-sm">S{ep.season} · {ep.runtime} min</p>
                  </div>
                  {ownEp ? (
                    <button data-testid={`ep-play-${ep.id}`} onClick={() => navigate(`/watch/${item.id}?ep=${ep.id}`)} className="bg-white text-black hover:bg-white/90 rounded-md font-semibold px-4 py-2 flex items-center gap-2 transition-colors shrink-0">
                      <Play className="w-4 h-4 fill-black" /> Play
                    </button>
                  ) : (
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`ep-rent-${ep.id}`} onClick={() => buy("rent", ep.id)} className="bg-brand hover:bg-brand/90 rounded-md text-sm font-semibold px-3 py-2 transition-colors">Rent ${epRent}</button>
                      <button data-testid={`ep-buy-${ep.id}`} onClick={() => buy("buy", ep.id)} className="bg-gold text-black hover:bg-gold/90 rounded-md text-sm font-semibold px-3 py-2 transition-colors">Buy ${epBuy}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10">
        <Row title="More Like This" items={related} />
      </div>

      {showTrailer && (
        <div data-testid="trailer-modal" className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setShowTrailer(false)}>
          <div className="w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <HlsVideo src={item.trailer_url} controls autoPlay className="w-full h-full" />
          </div>
        </div>
      )}
    </div>
  );
}
